import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

// Lista de páginas (base 1) com reticências: até 7 páginas mostra todas; acima disso, primeira,
// última e a vizinhança da atual.
export function buildPageList(page, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  if (page <= 3) {
    return [1, 2, 3, 4, "fim", totalPages];
  }
  if (page >= totalPages - 2) {
    return [1, "inicio", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "inicio", page - 1, page, page + 1, "fim", totalPages];
}

const formatNumber = (value) => Number(value || 0).toLocaleString("pt-BR");

const navButtonClass =
  "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background transition hover:border-foreground/30 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:text-muted-foreground";

export default function Pagination({
  page,
  pageSize,
  totalItems,
  totalPages: totalPagesProp,
  onPageChange,
  itemLabel = "itens",
  leading = null,
  disabled = false,
  bordered = true,
  hideWhenSinglePage = true,
  className,
}) {
  const [jumpValue, setJumpValue] = useState("");

  const total = Number(totalItems || 0);
  const totalPages = Math.max(1, Number(totalPagesProp) || Math.ceil(total / pageSize));
  if (hideWhenSinglePage && totalPages <= 1) return null;

  const current = Math.min(Math.max(page, 1), totalPages);
  const first = total > 0 ? (current - 1) * pageSize + 1 : 0;
  const last = Math.min(current * pageSize, total);
  const goTo = (target) => {
    const next = Math.min(Math.max(Math.trunc(Number(target)) || 1, 1), totalPages);
    if (next !== current) onPageChange(next);
  };

  const submitJump = () => {
    if (!jumpValue) return;
    goTo(jumpValue);
    setJumpValue("");
  };

  return (
    <nav
      aria-label="Paginação"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground",
        bordered && "border-t border-border pt-3",
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="tabular-nums">
          {formatNumber(first)}–{formatNumber(last)} de {formatNumber(total)} {itemLabel}
        </span>
        {leading}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {totalPages > 7 ? (
          <label className="mr-2 flex items-center gap-1.5">
            <span>Ir para</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={totalPages}
              value={jumpValue}
              placeholder={String(current)}
              disabled={disabled}
              onChange={(event) => setJumpValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  submitJump();
                }
              }}
              aria-label={`Ir para a página (1 a ${totalPages})`}
              className="h-8 w-16 rounded-lg border border-border bg-background px-2 text-xs tabular-nums text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            />
          </label>
        ) : null}

        <button
          type="button"
          className={navButtonClass}
          onClick={() => goTo(current - 1)}
          disabled={disabled || current === 1}
          aria-label="Página anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {buildPageList(current, totalPages).map((item) =>
          typeof item === "number" ? (
            <button
              key={`pagina-${item}`}
              type="button"
              onClick={() => goTo(item)}
              disabled={disabled}
              aria-current={item === current ? "page" : undefined}
              aria-label={`Página ${item}`}
              className={cn(
                "inline-flex h-8 min-w-8 items-center justify-center rounded-lg border px-2 text-xs font-semibold tabular-nums transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed",
                item === current
                  ? "border-border bg-muted text-foreground shadow-sm"
                  : "border-transparent text-muted-foreground hover:border-border hover:bg-background hover:text-foreground disabled:opacity-50"
              )}
            >
              {formatNumber(item)}
            </button>
          ) : (
            <span key={`reticencias-${item}`} className="inline-flex h-8 min-w-6 items-center justify-center" aria-hidden="true">
              …
            </span>
          )
        )}

        <button
          type="button"
          className={navButtonClass}
          onClick={() => goTo(current + 1)}
          disabled={disabled || current === totalPages}
          aria-label="Próxima página"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );
}
